// ---------------------------------------------------------------------------
// Admin login. Reuses secret-hitler's pattern: paste the Ed25519 private key,
// sign the server's challenge, exchange the signature for a session token.
//
// The private key is signed with in memory and never stored, never sent, and
// never written to localStorage. Only the resulting session token persists.
// ---------------------------------------------------------------------------

import * as ed from '@noble/ed25519';
import type { AdminAuthChallenge, AdminAuthSession } from '@countdown/shared';

/**
 * `generate-keys.sh` emits the private key as base64 DER (PKCS#8). The raw
 * 32-byte seed is the last 32 bytes of that structure.
 */
function seedFromPkcs8Base64(base64: string): Uint8Array {
  const der = Uint8Array.from(atob(base64.trim()), (c) => c.charCodeAt(0));
  if (der.length < 32) throw new Error('That does not look like an Ed25519 private key.');
  return der.slice(-32);
}

export async function adminLogin(privateKeyBase64: string): Promise<AdminAuthSession> {
  const seed = seedFromPkcs8Base64(privateKeyBase64);

  const challengeResponse = await fetch('/admin/api/challenge', { method: 'POST' });
  if (!challengeResponse.ok) throw new Error('Could not reach the admin endpoint.');
  const { challenge } = (await challengeResponse.json()) as AdminAuthChallenge;

  const publicKey = await ed.getPublicKeyAsync(seed);
  const signature = await ed.signAsync(new TextEncoder().encode(challenge), seed);

  const verifyResponse = await fetch('/admin/api/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      challenge,
      method: 'ed25519',
      publicKey: bytesToBase64(publicKey),
      ed25519Signature: bytesToBase64(signature),
    }),
  });

  if (!verifyResponse.ok) {
    const body = (await verifyResponse.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? 'Those credentials were rejected.');
  }

  return (await verifyResponse.json()) as AdminAuthSession;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

export async function adminFetch<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/admin/api${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `Admin request failed (${response.status})`);
  }
  return (await response.json()) as T;
}
